import { RefreshCw, Clock } from 'lucide-react';
import { useCurrentPrices } from '../hooks/useCurrentPrices';
import { usePriceRefresh } from '../hooks/usePriceRefresh';

interface PriceStatusIndicatorProps {
  className?: string;
  showDetails?: boolean;
}

export function PriceStatusIndicator({ className = '', showDetails = false }: PriceStatusIndicatorProps) {
  const { isPollingActive, lastUpdate } = useCurrentPrices();
  const { state: refreshState } = usePriceRefresh();

  const getStatusColor = () => {
    if (refreshState.isRefreshing) return 'text-blue-500';
    if (isPollingActive) return 'text-green-500';
    return 'text-orange-500';
  };

  const getStatusText = () => {
    if (refreshState.isRefreshing) return 'Refreshing prices...';
    if (isPollingActive) return 'Polling active';
    return 'API prices only';
  };

  const getStatusIcon = () => {
    if (refreshState.isRefreshing) {
      return <RefreshCw className="w-4 h-4 animate-spin" />;
    }
    return <RefreshCw className={`w-4 h-4 ${isPollingActive ? '' : 'text-orange-500'}`} />;
  };

  const timeSinceUpdate = lastUpdate ? Date.now() - lastUpdate : null;
  const isStale = timeSinceUpdate && timeSinceUpdate > 1 * 60 * 1000; // 1 minute (polling interval)

  if (!showDetails) {
    return (
      <div className={`flex items-center space-x-1 ${className}`}>
        <div className={getStatusColor()}>
          {getStatusIcon()}
        </div>
        {isStale && (
          <div title="Price data may be stale">
            <Clock className="w-3 h-3 text-orange-500" />
          </div>
        )}
      </div>
    );
  }

  return (
    <div className={`flex items-center space-x-2 text-sm ${className}`}>
      <div className={`flex items-center space-x-1 ${getStatusColor()}`}>
        {getStatusIcon()}
        <span>{getStatusText()}</span>
      </div>

      {lastUpdate && (
        <div className="text-xs text-gray-500 dark:text-gray-400">
          Last update: {new Date(lastUpdate).toLocaleTimeString()}
          {isStale && (
            <span className="ml-1 text-orange-500" title="Price data may be stale">
              (stale)
            </span>
          )}
        </div>
      )}

      {refreshState.error && (
        <div className="text-xs text-red-500">
          Error: {refreshState.error}
        </div>
      )}
    </div>
  );
}
